import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { reverse, sortBy } from 'lodash';
import { Flex, P, Pagination, Table } from '@decisiv/ui-components';

import buildExecutionColumns from './executionColumns';
import { executionPropTypes } from './executionPropTypes';
import { TableWrapper } from './style';

export default function ExecutionsListTable({
  currentPageExecutions,
  currentStartIndex,
  currentEndIndex,
  itemsPerPage,
  executions,
  totalPages,
  totalRecords,
  onPageChange,
  currentPage,
  setSelectedExecution,
  isRetryButtonVisible,
  isRetryButtonDisabled,
  handleRetry,
  retryExecutionIdRef,
  setSortedExecutions,
  getExecutions,
  setExecutionModalDetailsVisibility,
  tableColumns,
  maxRetryCount = 0,
}) {
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  const onSort = useCallback(
    (columnName) => (sortingType) => {
      switch (sortingType) {
        case 'ascending':
          setSortedExecutions(sortBy(executions, [columnName]));
          return;
        case 'descending':
          setSortedExecutions(reverse(sortBy(executions, [columnName])));
          return;
        case 'unsorted':
        default:
          setSortedExecutions(executions);
      }
    },
    [executions, setSortedExecutions],
  );

  const handleShowModal = (data) => {
    setHighlightedRowId(data.rowData.id);
    setSelectedExecution({ ...data.rowData.attributes, id: data.rowData.id });
    setExecutionModalDetailsVisibility(true);
  };

  const baseColumns = buildExecutionColumns({
    onSort,
    onShowDetails: handleShowModal,
    retry: {
      isRetryButtonVisible,
      isRetryButtonDisabled,
      handleRetry,
      retryExecutionIdRef,
      onRetried: getExecutions,
    },
    executions,
    maxRetryCount,
  });

  // Selects the requested columns in the order asked for, and marks the row the
  // details modal was opened from — the marker is what the stylesheet keys on.
  const columnsBuilder = (columns) =>
    columns
      .map((key) => baseColumns.find((item) => item.name === key))
      .filter((item) => item)
      .map((col) => ({
        ...col,
        DataCell: (data) => (
          <>
            {data.rowData.id === highlightedRowId && (
              <span data-highlighted-cell="" style={{ display: 'none' }} />
            )}
            {col.DataCell(data)}
          </>
        ),
      }));

  return (
    <TableWrapper id="ExecutionsListTable">
      <Table
        columns={columnsBuilder(tableColumns)}
        data={currentPageExecutions}
        kind="secondary"
        getRowKey={({ id }) => id}
        footer={() => (
          <Flex flex={1} alignItems="center" justifyContent="space-between">
            <Flex>
              <P>
                {currentStartIndex + 1} -{' '}
                {itemsPerPage > executions.length
                  ? currentStartIndex + executions.length
                  : currentEndIndex}{' '}
                of {totalRecords} Items
              </P>
            </Flex>
            <Pagination
              totalPages={totalPages}
              onPageChange={onPageChange}
              activePage={currentPage}
            />
          </Flex>
        )}
      />
    </TableWrapper>
  );
}

ExecutionsListTable.propTypes = {
  currentPageExecutions: PropTypes.arrayOf(executionPropTypes).isRequired,
  currentStartIndex: PropTypes.number.isRequired,
  currentEndIndex: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  executions: PropTypes.arrayOf(executionPropTypes).isRequired,
  totalPages: PropTypes.number.isRequired,
  totalRecords: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  setSelectedExecution: PropTypes.func.isRequired,
  isRetryButtonVisible: PropTypes.func.isRequired,
  isRetryButtonDisabled: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
  retryExecutionIdRef: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setSortedExecutions: PropTypes.func.isRequired,
  getExecutions: PropTypes.func.isRequired,
  setExecutionModalDetailsVisibility: PropTypes.func.isRequired,
  tableColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxRetryCount: PropTypes.number,
};
